import React from 'react';
import {useSelector} from 'react-redux';
import {ContextualMenu, ContextualMenuItemType, Icon, IContextualMenuItem} from '@fluentui/react';
import {RootState} from '../../store/rootReducer';
import {useApplyToWorkspaceItems} from './useApplyToWorkspaceItems';

interface ISourceHeaderProps {
    isExample?: boolean;
}

/**
 * The Source column header on the workspace grid. Its menu sets every product line's source at once,
 * to one of the two rules that apply to any product (a specific vendor only makes sense per line).
 * Bundles and ad-hoc lines have no source to set and are left alone.
 */
const SourceHeader: React.FC<ISourceHeaderProps> = (props) => {
    const selectedWorkspace = useSelector((state: RootState) => state.settings.selectedWorkspaceId);
    if (selectedWorkspace == null || props.isExample) return <>Source</>;
    return <SourceHeaderEntry workspace={selectedWorkspace} />;
};

const SourceHeaderEntry: React.FC<{workspace: string}> = ({workspace}) => {
    const [showMenu, setShowMenu] = React.useState(false);
    const headerRef = React.useRef<HTMLSpanElement>(null);
    const {apply, applying, items} = useApplyToWorkspaceItems(workspace);
    const productLines = items.filter((item) => !!item.normalized).length;

    const setAll = (source: 'LowestInStock' | 'LowestPrice', label: string) => {
        setShowMenu(false);
        void apply(`source "${label}"`, (item) => !!item.normalized, () => ({source, discountedCost: undefined}));
    };

    const menuItems: IContextualMenuItem[] = [
        {key: 'header', itemType: ContextualMenuItemType.Header, text: `Set all ${productLines} product line${productLines === 1 ? '' : 's'} to`},
        {key: 'LowestInStock', text: 'Lowest Price In Stock', iconProps: {iconName: 'Shop'}, disabled: applying || productLines === 0, onClick: () => setAll('LowestInStock', 'Lowest Price In Stock')},
        {key: 'LowestPrice', text: 'Lowest Price', iconProps: {iconName: 'Money'}, disabled: applying || productLines === 0, onClick: () => setAll('LowestPrice', 'Lowest Price')},
    ];

    return (
        <div onClick={() => setShowMenu(!showMenu)} style={{cursor: 'pointer'}} title='Set the source for every product line'>
            <span ref={headerRef}>
                Source
                <ContextualMenu items={menuItems} hidden={!showMenu} target={headerRef} onDismiss={() => setShowMenu(false)} />
                <Icon iconName='Edit' style={{marginLeft: '0.5em'}} />
            </span>
        </div>
    );
};

export default SourceHeader;
